import abi from 'ethereumjs-abi';
import { EipError } from '../types';
import { storePreferredWalletProviderName } from './session';
import { checkWalletInstalled, ensureCorrectChain } from './ethereum.util';
import { getCurrentConnector, tryConnectWallet } from './walletClient';
import thetanCoinContract from '@wolffun/shared/src/contracts/thetaCoin';
import busdContract from '@wolffun/shared/src/contracts/busd';
import busdtContract from '@wolffun/shared/src/contracts/busdt';
import wbnbContract from '@wolffun/shared/src/contracts/wbnb';
import thetanGemContract from '@wolffun/shared/src/contracts/thetaGem';
import { blockchainNumberToSystemCurrency, CurrencyType } from '@wolffun/system-currency';
import BN from 'bn.js';
import { THETA_CHAIN_ID_NUMBER } from '@wolffun/contract-address';

const getContract = type => {
  switch (type) {
    case CurrencyType.BUSD:
      return busdContract;

    case CurrencyType.BUSDT:
      return busdtContract;

    case CurrencyType.WBNB:
      return wbnbContract;

    case CurrencyType.THC:
      return thetanCoinContract;

    case CurrencyType.THG:
      return thetanGemContract;

    default:
      throw new Error('Currency contract is not supported!');
  }
};

export const ensureProvider = async options => {
  const {
    ensureCorrectChainId,
    ensureAddedThetaCoin,
    preferred
  } = options || {};
  const error = checkWalletInstalled(preferred);

  if (error) {
    return {
      error
    };
  }

  const {
    error: connectError
  } = await tryConnectWallet(preferred);

  if (connectError) {
    return {
      error: connectError
    };
  }

  if (preferred) {
    storePreferredWalletProviderName(preferred);
  }

  if (ensureCorrectChainId) {
    const error = await ensureCorrectChain();

    if (error) {
      return error;
    }
  }

  if (ensureAddedThetaCoin) {
    try {
      var _getCurrentConnector$, _getCurrentConnector;

      const wasAdded = await ((_getCurrentConnector$ = (_getCurrentConnector = getCurrentConnector()).watchAsset) === null || _getCurrentConnector$ === void 0 ? void 0 : _getCurrentConnector$.call(_getCurrentConnector, {
        address: thetanCoinContract.address,
        symbol: thetanCoinContract.symbol
      }));

      if (!wasAdded) {
        return {
          error: EipError.THETA_COIN_NOT_ADDED
        };
      }
    } catch (err) {
      return {
        error: err.code
      };
    }
  }

  return {
    error: EipError.NO_ERROR
  };
};
export const getBalance = async (address, type) => {
  try {
    const contract = await getContract(type).getContract();
    const balance = await contract.methods.balanceOf(address).call({
      from: address
    });
    return {
      error: EipError.NO_ERROR,
      balance: blockchainNumberToSystemCurrency(type, new BN(balance))
    };
  } catch (err) {
    return {
      error: EipError.UNSUPPORTED_METHOD
    };
  }
};
export const getThetaBalance = async address => {
  const contract = await thetanCoinContract.getContract();

  try {
    const balance = await contract.methods.balanceOf(address).call({
      from: address
    });
    return {
      error: EipError.NO_ERROR,
      balance: blockchainNumberToSystemCurrency(CurrencyType.THC, new BN(balance))
    };
  } catch (err) {
    return {
      error: EipError.UNSUPPORTED_METHOD
    };
  }
};
export const personalSign = async params => {
  try {
    const {
      address,
      data
    } = params;
    const {
      error
    } = await ensureProvider();

    if (error !== EipError.NO_ERROR) {
      return {
        error
      };
    }

    const signature = await getCurrentConnector().signMessage({
      address: address,
      message: data
    });
    return {
      error: EipError.NO_ERROR,
      signature
    };
  } catch (err) {
    return {
      error: err.code
    };
  }
};
export const getAccounts = async () => {
  try {
    const address = await getCurrentConnector().getAccount();

    if (!address) {
      return {
        error: EipError.METAMASK_LOCKED
      };
    }

    return {
      error: EipError.NO_ERROR,
      accounts: [address]
    };
  } catch (err) {
    return {
      error: err.code
    };
  }
};
export const switchToBSC = async () => {
  try {
    const {
      error
    } = await ensureProvider();

    if (error !== EipError.NO_ERROR) {
      return {
        error
      };
    }

    await getCurrentConnector().switchChain(THETA_CHAIN_ID_NUMBER);
  } catch (err) {
    const error = err.code;
    return {
      error
    };
  }

  return {
    error: EipError.NO_ERROR
  };
};
export const hashMessage = (args, data) => {
  return '0x' + abi.soliditySHA3(args, data).toString('hex');
};